/* Candidate resolver. Deliberately independent of DOM, Shopify ranking and inventory. */
(function (root) {
  'use strict';
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const words = value => normalize(value).match(/[a-z0-9]+/g) || [];
  const ignored = new Set(['de','del','el','la','los','las','y','en','para','con']);
  const plastics = new Set(['pp','pet','pet / plastico','polietileno','pvc','vinilo','plastico','polipropileno no tejido']);
  // Directional category membership. Never infer it from titles, tags or body.
  const cutlery = ['tenedor','cuchara','cuchillo','set de cubiertos'];
  const categories = [
    {terms:['cubierto','cubiertos'], families:new Set(cutlery)},
  ];
  const categoryFor = term => term.length >= 3
    ? categories.filter(category=>category.terms.some(word=>word.startsWith(term))) : [];
  function tokens(value) { return [...new Set(words(value).filter(w => !ignored.has(w)))]; }
  function oneEdit(a,b) {
    if (a===b) return true;
    if (Math.abs(a.length-b.length)>1) return false;
    if(a.length===b.length) {
      const different=[];for(let i=0;i<a.length;i++) if(a[i]!==b[i]) different.push(i);
      if(different.length===1) return true;
      return different.length===2 && different[1]===different[0]+1 && a[different[0]]===b[different[1]] && a[different[1]]===b[different[0]];
    }
    const shorter=a.length<b.length?a:b, longer=a.length<b.length?b:a;
    let i=0;while(i<shorter.length && shorter[i]===longer[i]) i++;
    return shorter.slice(i)===longer.slice(i+1);
  }
  function createIndex(source) {
    const products=source.map(p=>{
      const materialWords=tokens(p.material);
      if(plastics.has(normalize(p.material))) materialWords.push('plastico','plastica','plasticos','plasticas');
      const categoryWords=categories.filter(c=>c.families.has(normalize(p.family))).flatMap(c=>c.terms);
      const text=[...tokens([p.title,p.family].join(' ')),...materialWords,...categoryWords];
      return {...p, words:[...new Set(text)],materialWords, codes:(p.skus||[]).filter(Boolean).map(s=>normalize(s))};
    });
    const vocabulary=[...new Set(products.flatMap(p=>p.words))];
    const allFamilies=[...new Set(products.map(p=>normalize(p.family)))].filter(Boolean);
    const singular = token => {
      // A valid prefix such as plas must not be shortened to the word PLA.
      if(vocabulary.some(w=>w.startsWith(token))) return token;
      if(token.endsWith('es') && vocabulary.includes(token.slice(0,-2))) return token.slice(0,-2);
      if(token.endsWith('s') && vocabulary.includes(token.slice(0,-1))) return token.slice(0,-1);
      return token;
    };
    const materialVocabulary=[...new Set(products.flatMap(p=>p.materialWords))];
    const matches=(p,query)=>query.every(t=>{
      const groups=categoryFor(t);
      if(groups.length) return groups.some(group=>group.families.has(normalize(p.family)));
      const familyPrefix=allFamilies.some(f=>f.startsWith(t));
      const isMaterial=!familyPrefix && (t.length>=3?materialVocabulary.some(w=>w.startsWith(t)):materialVocabulary.includes(t));
      return (isMaterial?p.materialWords:p.words).some(w=>w.startsWith(t));
    });
    function resolve(raw, allowCorrection = true) {
      const original=normalize(raw);
      if(!original) return {products:[],mode:'empty',correction:null};
      const exactCodes=products.filter(p=>p.codes.includes(original));
      if(exactCodes.length) return {products:exactCodes,mode:'sku',correction:null,
        skuCodes:exactCodes.flatMap(p=>p.skus.filter(s=>normalize(s)===original))};
      if(/^[a-z0-9._/-]{3,}$/.test(original)) {
        const codes=products.filter(p=>p.codes.some(c=>c.startsWith(original)));
        if(codes.length) return {products:codes,mode:'sku-prefix',correction:null,
          skuCodes:codes.flatMap(p=>p.skus.filter(s=>normalize(s).startsWith(original)))};
      }
      const terms=tokens(original).map(singular);
      if(!terms.length) return {products:[],mode:'empty',correction:null};
      const fullTitles=products.filter(p=>normalize(p.title)===original);
      if(fullTitles.length) return {products:fullTitles,mode:'title',correction:null};
      // A leading, unambiguous product family expresses the requested object.
      // Material labels (for example Papel Kraft) must not become family filters.
      const isMaterial=products.some(p=>normalize(p.material)===original) || materialVocabulary.some(w=>w.startsWith(terms[0]));
      const phraseFamily=allFamilies.filter(f=>f.includes(' ') && (original===f || original.startsWith(f+' '))).sort((a,b)=>b.length-a.length)[0];
      const families=phraseFamily?[phraseFamily]:allFamilies.filter(f=>!f.includes(' ') && terms[0].length>=3 && f.startsWith(terms[0]));
      const pool=!isMaterial && families.length===1?products.filter(p=>normalize(p.family)===families[0]):products;
      const direct=pool.filter(p=>matches(p,terms));
      if(direct.length) return {products:direct,mode:'direct',correction:null};
      if(!allowCorrection) return {products:[],mode:'none',correction:null};
      const possibilities=new Map();
      for(let i=0;i<terms.length;i++) {
        const term=terms[i];
        if(term.length<5 || /\d/.test(term)) continue;
        // Correct at most one term; all other terms must still match the same product.
        const others=terms.filter((_,j)=>j!==i);
        const context=pool.filter(p=>matches(p,others));
        for(const candidate of new Set(context.flatMap(p=>p.words))) {
          if(candidate[0]!==term[0] || /\d/.test(candidate)) continue;
          if(!oneEdit(term,candidate) && !(candidate.length>term.length && oneEdit(term,candidate.slice(0,term.length)))) continue;
          const corrected=[...terms];corrected[i]=candidate;
          // Reapply family/material rules after correction: vasoo -> vaso must
          // not admit lids merely because their titles mention vasos.
          const hits=resolve(corrected.join(' '),false).products;
          if(!hits.length) continue;
          const key=hits.map(p=>p.handle).sort().join('\n');
          if(!possibilities.has(key)) possibilities.set(key,{products:hits,mode:'corrected',correction:corrected.join(' ')});
        }
      }
      if(possibilities.size===1) return [...possibilities.values()][0];
      return {products:[],mode:possibilities.size?'ambiguous':'none',correction:null,
              alternatives:[...possibilities.values()].map(p=>p.correction).slice(0,3)};
    }
    return {products,vocabulary,resolve};
  }
  function compile(result) {
    if(!result.products.length) return 'variants.sku:"__AP_EMPTY__" AND -variants.sku:"__AP_EMPTY__"';
    const quote=s=>'"'+String(s).replace(/\\/g,'\\\\').replace(/"/g,'\\"')+'"';
    const skus=result.skuCodes || result.products.map(p=>(p.skus||[]).find(Boolean));
    if(skus.some(s=>!s)) throw new Error('Un producto coincidente no tiene SKU. No se puede preparar una búsqueda exacta.');
    const query=[...new Set(skus)].map(s=>'variants.sku:'+quote(s)).join(' OR ');
    return query;
  }
  const api={normalize,tokens,oneEdit,createIndex,compile};
  if(typeof module!=='undefined' && module.exports) module.exports=api;
  else root.AlmaSearchCore=api;
})(typeof globalThis!=='undefined'?globalThis:this);
